import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import lv from './locales/lv.json';

export const SUPPORTED_LANGUAGES = ['lv', 'en'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

const LANGUAGE_STORAGE_KEY = 'soc-dashboard-language';
const DEFAULT_LANGUAGE: SupportedLanguage = 'lv';

const resources = {
  en: { translation: en },
  lv: { translation: lv },
};

/**
 * Get the initial language from URL, localStorage, or default
 */
function getInitialLanguage(): SupportedLanguage {
  // Check URL first (highest priority)
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get('lang');
  if (urlLang && SUPPORTED_LANGUAGES.includes(urlLang as SupportedLanguage)) {
    return urlLang as SupportedLanguage;
  }

  // Check localStorage (user's previous selection)
  const cachedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (cachedLang && SUPPORTED_LANGUAGES.includes(cachedLang as SupportedLanguage)) {
    return cachedLang as SupportedLanguage;
  }

  // Default language (Latvian)
  return DEFAULT_LANGUAGE;
}

/**
 * Map Teams locale to supported language
 */
export function mapTeamsLocale(teamsLocale: string | undefined): SupportedLanguage {
  if (!teamsLocale) return DEFAULT_LANGUAGE;
  
  const lang = teamsLocale.split('-')[0].toLowerCase();
  if (SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
    return lang as SupportedLanguage;
  }
  
  return DEFAULT_LANGUAGE;
}

/**
 * Change language and update URL + localStorage
 */
export function changeLanguage(lang: SupportedLanguage): void {
  i18n.changeLanguage(lang);
  localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  
  // Update URL with new language
  const url = new URL(window.location.href);
  url.searchParams.set('lang', lang);
  window.history.replaceState({}, '', url.toString());
}

/**
 * Initialize language from Teams context
 * Should be called after Teams SDK initialization
 */
export function initLanguageFromTeams(teamsLocale: string | undefined): void {
  // Only use Teams locale if no URL or localStorage override
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get('lang');
  const cachedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  
  if (!urlLang && !cachedLang && teamsLocale) {
    const lang = mapTeamsLocale(teamsLocale);
    i18n.changeLanguage(lang);
    
    // Update URL to reflect language
    const url = new URL(window.location.href);
    url.searchParams.set('lang', lang);
    window.history.replaceState({}, '', url.toString());
  }
}

/**
 * Get current language
 */
export function getCurrentLanguage(): SupportedLanguage {
  return i18n.language as SupportedLanguage;
}

i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLanguage(),
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
});

export default i18n;
